import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import MagneticButton from "@/components/site/MagneticButton";
import HeroCoverflow from "@/components/site/HeroCoverflow";
import { useSiteContent } from "@/lib/SiteContentProvider";
import { cleanText } from "@/lib/utils";

/**
 * Hero — light, centred "editorial" hero inspired by the client's reference.
 *
 *  • Off-white stage (#F7F5F0) with a faint warm→green wash and fine grain.
 *  • A single confident Montserrat headline, sub-line and CTAs — all centred.
 *  • A tilted, continuously-gliding coverflow of the studio's portfolio work
 *    sits beneath the copy (HeroCoverflow), giving depth and motion.
 *
 * The section re-scopes --pg-* to a light palette (see .pg-herolux in CSS) so
 * the shared buttons render dark-on-light even though the rest of the site runs
 * the dark cinematic theme.
 */
const fade = (delay, reduce) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduce ? 0.4 : 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = ({ onStartProject, onViewPortfolio }) => {
    const { data } = useSiteContent();
    const reduce = useReducedMotion();
    const hero = data?.hero || {};

    const description = cleanText(
        hero.description ||
        "High-end logo systems, brand identities, and visual direction for businesses ready to look more premium and unforgettable."
    );
    const primaryLabel = hero.primary_cta?.label || "View Portfolio";
    const ghostLabel = hero.ghost_cta?.label || "Start A Project";

    return (
        <section id="home" className="pg-herolux" data-testid="hero-section">
            <div className="pg-herolux__wash" aria-hidden="true" />
            <div className="pg-herolux__grain" aria-hidden="true" />

            <div className="pg-herolux__content">
                <motion.span {...fade(0.05, reduce)} className="pg-herolux__badge" data-testid="hero-eyebrow">
                    <span className="pg-herolux__badge-dot" aria-hidden="true" />
                    Premium brand &amp; identity studio
                </motion.span>

                <motion.h1 {...fade(0.12, reduce)} className="pg-herolux__title" data-testid="hero-headline">
                    Building brands that look premium, feel strategic, and stay memorable.
                </motion.h1>

                <motion.p {...fade(0.22, reduce)} className="pg-herolux__sub" data-testid="hero-subtext">
                    {description}
                </motion.p>

                <motion.div {...fade(0.32, reduce)} className="pg-herolux__ctas">
                    <MagneticButton
                        type="button"
                        onClick={() => onViewPortfolio?.()}
                        data-testid="hero-view-portfolio-button"
                        className="pg-btn-primary"
                    >
                        {primaryLabel}
                        <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                    </MagneticButton>
                    <MagneticButton
                        type="button"
                        onClick={() => onStartProject?.()}
                        data-testid="hero-start-project-button"
                        className="pg-btn-ghost"
                    >
                        <span>{ghostLabel}</span>
                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.8} />
                    </MagneticButton>
                </motion.div>
            </div>

            <HeroCoverflow />
        </section>
    );
};

export default Hero;
